import { useMemo } from "react";
import { IoClose } from 'react-icons/io5';
import { formatSalesValue } from "../../utils/formatUtils";
import { convertTime } from "../../utils/timeUtils";

const SectionHeader = ({ icon, title, badge }) => (
    <div className="flex items-center gap-2.5 bg-white">
        <span className="text-base font-semibold text-zinc-900">
            {title}
        </span>

        {badge && (
            <span className="ml-auto rounded-full text-sm bg-zinc-100 px-2.5 py-1 font-semibold text-zinc-700">
                {badge}
            </span>
        )}
    </div>
);

const Row = ({ label, value, sub, bold, last }) => (
    <div
        className={`
            grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-2.5
            ${!last ? "border-b border-zinc-200" : ""}
        `}
    >
        <div>
            <div
                className={`
                    ${bold ? "font-semibold" : "font-medium"}
                    text-zinc-900
                    ${sub ? "mb-1" : ""}
                `}
            >
                {label}
            </div>

            {sub && (
                <div className="text-sm leading-relaxed text-zinc-700 font-semibold">
                    {sub}
                </div>
            )}
        </div>

        <div
            className={`
                whitespace-nowrap
                ${bold ? "font-semibold" : "font-semibold"}
                text-zinc-900
            `}
        >
            {value}
        </div>
    </div>
);

const Card = ({ children, className = "" }) => (
    <div
        className={`
            overflow-hidden rounded-2xl
            border border-zinc-200
            bg-white
            shadow-sm
            ${className}
        `}
    >
        {children}
    </div>
);

const Pill = ({ children }) => (
    <span
        className="
            inline-flex items-center
            rounded-full
            border border-zinc-200
            bg-zinc-50
            px-3 py-1.5
            text-[13px] font-medium
            text-zinc-700
        "
    >
        {children}
    </span>
);

// ─── Main Modal ──────────────────────────────────────────────────────────────

const SidebarDetailModal = ({
    onClose,
    dineinTabs,
    paymentStatus,
    startBillNum,
    endBillNum,
    outlet,
    creditOrders,
    discountOrders,
    complimentaryItems,
    compLoading,
    compError,
    voidedOrders,
    voidedLoading,
    voidedError,
    totalSplitCash,
    totalSplitCreditCard,
    totalSplitMobilePayment,
    isServiceChecked,
    startDate,
    endDate,
    revenuePerGuest,
}) => {
    const adjustedNetTotal = isServiceChecked
        ? Number(dineinTabs?.netTOTALSALES || 0) - Number(dineinTabs?.TotalServiceCharge || 0)
        : Number(dineinTabs?.netTOTALSALES || 0);
    const adjustedNetDineIn = isServiceChecked
        ? Number(dineinTabs?.netDineInSALES || 0) - Number(dineinTabs?.DineInServiceCharge || 0)
        : Number(dineinTabs?.netDineInSALES || 0);
    const adjustedNetTab = isServiceChecked
        ? Number(dineinTabs?.netTabSALES || 0) - Number(dineinTabs?.TabServiceCharge || 0)
        : Number(dineinTabs?.netTabSALES || 0);

    const creditTotal = useMemo(
        () => creditOrders.reduce((s, o) => s + Number(o.Total || 0), 0),
        [creditOrders]
    );
    const discountTotal = useMemo(
        () => discountOrders.reduce((s, o) => s + Number(o.DiscountAmt || 0), 0),
        [discountOrders]
    );

    const reopenedBills = useMemo(
        () => dineinTabs?.reopened_bills ?? [],
        [dineinTabs?.reopened_bills]
    );

    const customers = complimentaryItems?.customers || [];
    const compFoodTotal = customers.reduce((s, c) => s + Number(c.type_summary?.Food?.total_amount || 0), 0);
    const compBevTotal = customers.reduce((s, c) => s + Number(c.type_summary?.Beverage?.total_amount || 0), 0);

    const formatDate = (date) => {
        const d = new Date(date);
        const day = d.getDate();

        const getOrdinal = (n) => {
            if (n > 3 && n < 21) return "th";

            switch (n % 10) {
                case 1:
                    return "st";
                case 2:
                    return "nd";
                case 3:
                    return "rd";
                default:
                    return "th";
            }
        };

        const month = d.toLocaleString("en-GB", { month: "long" });
        const year = d.getFullYear();

        return `${day}${getOrdinal(day)} ${month}, ${year}`;
    };

    const dateDisplay =
        startDate === endDate
            ? `Date: ${formatDate(startDate)}`
            : `From : ${formatDate(startDate)} | To : ${formatDate(endDate)}`;

    const companyName = localStorage.getItem("selectedCompany");

    const handleExportTxt = () => {
        const WIDTH = 44;
        const line = (c = "-", l = WIDTH) => c.repeat(l);
        const pad = (label, value, width = WIDTH) => {
            const l = String(label), v = String(value);
            return l + " ".repeat(Math.max(1, width - l.length - v.length)) + v;
        };
        const f = (val) =>
            val !== undefined && val !== null && val !== ""
                ? Number(val).toFixed(2)
                : "0.00";

        const center = (text, width = WIDTH) => {
            const t = String(text);
            const total = width - t.length;
            if (total <= 0) return t;
            const left = Math.floor(total / 2);
            const right = total - left;
            return " ".repeat(left) + t + " ".repeat(right);
        };

        const sectionHeader = (text, fill = "*", width = WIDTH) => {
            const inner = ` ${text} `;
            const total = width - inner.length;
            if (total <= 0) return inner.trim();
            const left = Math.floor(total / 2);
            const right = total - left;
            return fill.repeat(left) + inner + fill.repeat(right);
        };

        const rows = [];
        rows.push(`\n${sectionHeader(companyName)}`);
        rows.push(`\n${sectionHeader(outlet)}`);
        rows.push(`\n${sectionHeader(dateDisplay)}\n`);

        rows.push(line());

        rows.push(`\n    Total Sale : ${f(dineinTabs.TOTALSALES)}`);
        rows.push(`     Dine - In : ${f(dineinTabs.DineInSALES)}`);
        rows.push(`          Tabs : ${f(dineinTabs.TabSALES || 0)}`);

        rows.push(line());

        rows.push(`\nTotal Net Sale : ${f(adjustedNetTotal)}`);
        rows.push(`     Dine - In : ${f(adjustedNetDineIn)}`);
        rows.push(`          Tabs : ${f(adjustedNetTab)}`);

        rows.push(line());

        rows.push(`\n           VAT : ${f(dineinTabs.TotalVat)}`);
        rows.push(`     Dine - In : ${f(dineinTabs.DineInVAT)}`);
        rows.push(`          Tabs : ${f(dineinTabs.TabVAT || 0)}`);
        rows.push(line());
        if (isServiceChecked) {
            rows.push(`\nService Charge : ${f(dineinTabs.TotalServiceCharge)}\n`);
            rows.push(pad("     Dine - In :", f(dineinTabs.DineInServiceCharge)));
            rows.push(pad("          Tabs :", f(dineinTabs.TabServiceCharge || 0)));
            rows.push(line());
        }
        rows.push(`\n      Food Sale : ${f(dineinTabs.FoodSale)}`);
        rows.push(`\n  Beverage Sale : ${f(dineinTabs.BeverageSale)}`);
        rows.push(`\n          Other : ${dineinTabs.OtherSale || ""}`);
        rows.push(line());
        rows.push(`\n    Credit Card : ${f(Number(paymentStatus.CreditCard || 0) + totalSplitCreditCard)}`);
        rows.push(`\n           Cash : ${f(Number(paymentStatus.Cash || 0) + totalSplitCash)}`);
        rows.push(`\n Mobile Payment : ${f(Number(paymentStatus.MobilePayment || 0) + totalSplitMobilePayment)}`);
        rows.push(`\n   Total Guests : ${dineinTabs.TotalGuests || "-"}`);
        rows.push(`\nDiscount Amount : ${f(dineinTabs.DiscountAmountSum)}`);
        rows.push(`\n    Credit Sale : ${f(paymentStatus.Credit)}\n`);
        rows.push(line());

        // ── Credit Bills ──────────────────────────────────────────
        rows.push(`\n${sectionHeader("Credit Bills")}`);
        rows.push(line());
        rows.push("BillNo   Name                        Total");
        rows.push("------   -----------------------     -------");
        creditOrders.forEach((o) => {
            rows.push(
                `${String(o.bill_no || "").padEnd(7)}  ${String(o.GuestName || "").padEnd(26)}  ${f(o.Total)}`
            );
        });

        rows.push(`\n`);
        rows.push(line());
        // ── Discount List ─────────────────────────────────────────
        rows.push(`\n${sectionHeader("DISCOUNT LIST")}`);
        rows.push(line());
        discountOrders.forEach((o) => {
            rows.push(`\n    Guest Name : ${o.GuestName || ""}\n`);
            rows.push(`       Bill No : ${o.bill_no || ""}`);
            rows.push(`      Discount : Regular`);
            rows.push(`  Discount Amt : ${f(o.DiscountAmt)}`);
            rows.push(line());
        });

        // ── Complimentary / NC ────────────────────────────────────
        rows.push(`\n`);
        rows.push(line());
        rows.push(`${sectionHeader("Complimentary/NC")}`);
        rows.push(line());
        customers.forEach((c) => {
            rows.push(`\n   Guest : ${c.GuestName}\n`);
            Object.values(c.items_by_type || {})
                .flat()
                .forEach((item) => {
                    rows.push(`    Item : ${item.total_count} X ${item.ItemName}`);
                    rows.push(`   Value : ${f(item.total_amount)}\n`);
                });
            rows.push(line());
        });
        rows.push(`\n    Total Food : ${f(compFoodTotal)}`);
        rows.push(`Total Beverage : ${f(compBevTotal)}`);
        rows.push(`   Grand Total : ${f(compFoodTotal + compBevTotal)}`);

        // ── Bill Transfers ────────────────────────────────────────
        rows.push(`\n${line()}`);
        rows.push(sectionHeader("BILL TRANSFERS"));
        rows.push(line());

        // ── Void Details ──────────────────────────────────────────
        rows.push(`\n${sectionHeader("Void Details")}`);
        rows.push(line());
        if (voidedOrders.length > 0) {
            voidedOrders.forEach((order) => {
                (order.items || []).forEach((item) => {
                    rows.push(`\nEmployee : ${order.Employee || "—"}`);
                    rows.push(`    Item : ${item.ItemName}`);
                    rows.push(`Quantity : ${item.Quantity}`);
                    rows.push(`  Reason : ${item.reason || "—"}`);
                    rows.push(`Order ID : ${order.Outlet_orderID || "—"}`);
                    rows.push(`   Value : ${f(Number(item.item_price) * Number(item.Quantity))}`);
                    rows.push(line());
                });
            });
        } else {
            rows.push("No voided orders");
            rows.push(line());
        }

        // ── Order Reopens ─────────────────────────────────────────
        rows.push(`\n${sectionHeader("Order Reopens")}`);
        rows.push(line());
        if (reopenedBills.length > 0) {
            reopenedBills.forEach((o) => {
                rows.push(`\n   Order ID : ${o.Outlet_OrderID || "—"}`);
                rows.push(`    Bill No : ${o.bill_no || "—"}`);
                rows.push(`Opened Time : ${o.Opened_Time || "—"}`);
                rows.push(line());
            });
        } else {
            rows.push("No reopened bills");
            rows.push(line());
        }

        // ── Footer ────────────────────────────────────────────────
        rows.push(`\nStarting Bill # : ${startBillNum}`);
        rows.push(`\n  Ending Bill # : ${endBillNum || "-"}`);
        rows.push(line());
        rows.push(
            `\n${line("*")}\n${center("END OF REPORT")}\n${line("*")}\n. Thank you\n\n`
        );

        const blob = new Blob([rows.join("\n")], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(outlet || "outlet")
            .replace(/[^a-z0-9]/gi, "_")
            .toLowerCase()}_report_${new Date().toLocaleDateString("en-CA")}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="flex h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3">
                    <div>
                        <div className="text-lg font-semibold text-gray-900">
                            {outlet || "Outlet"}
                        </div>

                        <div className="mt-1 text-sm text-gray-600">
                            {dateDisplay}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleExportTxt}
                            className="rounded-lg bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-100"
                        >
                            Export
                        </button>

                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                        >
                            <IoClose size={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto tiny-scrollbar bg-gray-50 py-4 px-6">
                    <div className="space-y-5">

                        {/* Metrics */}
                        <div className="grid gap-2.5 grid-cols-4">
                            {[
                                { label: "Starting bill", value: startBillNum ? `#${startBillNum}` : "—" },
                                { label: "Ending bill", value: endBillNum ? `#${endBillNum}` : "—" },
                                { label: "Total guests", value: dineinTabs.TotalGuests || "—" },
                                { label: "Revenue per guest", value: formatSalesValue(revenuePerGuest) || "—" },
                            ].map(({ label, value }) => (
                                <div
                                    key={label}
                                    className="rounded-lg border border-gray-200 bg-white px-3 py-3.5 shadow-sm"
                                >
                                    <div className="text-xs font-medium uppercase tracking-wide text-gray-600">
                                        {label}
                                    </div>

                                    <div className="mt-1 text-lg font-semibold text-gray-900">
                                        {value}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Sales Summary */}
                        <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm text-base">
                            <div className="border-b border-gray-100 px-4 py-2.5">
                                <SectionHeader
                                    title="Sales Summary"
                                />
                            </div>

                            <Row
                                label="Total sales"
                                bold
                                sub={`Dine-in : ${formatSalesValue(dineinTabs.DineInSALES)} · Take-away : ${formatSalesValue(dineinTabs.TabSALES || 0)}`}
                                value={formatSalesValue(dineinTabs.TOTALSALES)}
                            />

                            <Row
                                label="Net sales"
                                bold
                                sub={`Dine-in : ${formatSalesValue(adjustedNetDineIn)} · Take-away : ${formatSalesValue(adjustedNetTab)}`}
                                value={formatSalesValue(adjustedNetTotal)}
                            />

                            <Row
                                label="VAT"
                                sub={`Dine-in : ${formatSalesValue(dineinTabs.DineInVAT)} · Take-away : ${formatSalesValue(dineinTabs.TabVAT || 0)}`}
                                value={formatSalesValue(dineinTabs.TotalVat)}
                                last={!isServiceChecked}
                            />

                            {isServiceChecked && (
                                <Row
                                    label="Service charge"
                                    sub={`Dine-in : ${formatSalesValue(dineinTabs.DineInServiceCharge)} · Take-away : ${formatSalesValue(dineinTabs.TabServiceCharge || 0)}`}
                                    value={formatSalesValue(dineinTabs.TotalServiceCharge)}
                                    last
                                />
                            )}
                        </Card>

                        {/* Breakdown + Payments */}
                        <div className="grid gap-3 grid-cols-2 text-sm">

                            <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                                <div className="border-b border-gray-100 px-4 py-2.5">
                                    <SectionHeader
                                        icon="tools-kitchen-2"
                                        title="F&B Breakdown"
                                    />
                                </div>

                                <Row label="Food" value={formatSalesValue(dineinTabs.FoodSale)} />
                                <Row label="Beverage" value={formatSalesValue(dineinTabs.BeverageSale)} />
                                <Row label="Others" value={dineinTabs.OtherSale || "—"} last />
                            </Card>

                            <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                                <div className="border-b border-gray-100 px-4 py-2.5">
                                    <SectionHeader
                                        icon="credit-card"
                                        title="Payments"
                                    />
                                </div>

                                <Row
                                    label="Credit card"
                                    value={formatSalesValue(Number(paymentStatus.CreditCard || 0) + totalSplitCreditCard)}
                                />

                                <Row
                                    label="Cash"
                                    value={formatSalesValue(Number(paymentStatus.Cash || 0) + totalSplitCash)}
                                />

                                <Row
                                    label="Mobile"
                                    value={formatSalesValue(Number(paymentStatus.MobilePayment || 0) + totalSplitMobilePayment)}
                                />

                                <Row
                                    label="Credit sale"
                                    value={formatSalesValue(paymentStatus.Credit)}
                                />

                                <Row
                                    label="Complimentary"
                                    value={formatSalesValue(paymentStatus.Complimentary)}
                                />

                                <Row
                                    label="Discount"
                                    value={formatSalesValue(dineinTabs.DiscountAmountSum)}
                                    last
                                />
                            </Card>
                        </div>

                        {/* Credit Bills */}
                        <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-100 px-4 py-2.5">
                                <SectionHeader
                                    title="Credit Bills"
                                    badge={`${creditOrders.length} bill${creditOrders.length !== 1 ? "s" : ""} · ${formatSalesValue(creditTotal)} total`}
                                />
                            </div>

                            <div className="max-h-[500px] overflow-auto tiny-scrollbar">
                                <table className="w-full text-center text-sm">
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr className="border-b border-gray-200">
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Bill
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Guest Name
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {creditOrders.length > 0 ? (
                                            creditOrders.map((o, i) => (
                                                <tr
                                                    key={o.id || i}
                                                    className="border-b border-gray-100 last:border-0 font-semibold"
                                                >
                                                    <td className="px-4 py-2.5 text-gray-700">
                                                        {o.bill_no}
                                                    </td>

                                                    <td className="px-4 py-2.5 text-gray-900">
                                                        {o.GuestName || "—"}
                                                    </td>

                                                    <td className="px-4 py-2.5 text-gray-900">
                                                        {formatSalesValue(o.Total)}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={3}
                                                    className="px-4 py-8 text-center text-sm text-gray-600"
                                                >
                                                    No credit orders
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        {/* Discounts */}
                        <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-100 px-4 py-2.5">
                                <SectionHeader
                                    title="Discounts"
                                    badge={`${discountOrders.length} bill${discountOrders.length !== 1 ? "s" : ""} · ${formatSalesValue(discountTotal)} total`}
                                />
                            </div>

                            <div className="max-h-[500px] overflow-auto tiny-scrollbar">
                                <table className="w-full text-sm text-center">
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr className="border-b border-gray-200">
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Bill
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Guest Name
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Discount Type
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Discount Amount
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {discountOrders.length > 0 ? (
                                            discountOrders.map((o, i) => (
                                                <tr
                                                    key={o.id || i}
                                                    className="border-b border-gray-100 last:border-0 font-semibold"
                                                >
                                                    <td className="px-4 py-2.5 text-gray-700">
                                                        {o.bill_no}
                                                    </td>

                                                    <td className="px-4 py-2.5 text-gray-900">
                                                        {o.GuestName || "—"}
                                                    </td>

                                                    <td className="px-4 py-2.5 text-gray-700">
                                                        {o.discounttype || "—"}
                                                    </td>

                                                    <td className="px-4 py-2.5 text-gray-900">
                                                        {formatSalesValue(o.DiscountAmt)}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="px-4 py-8 text-center text-sm text-gray-600"
                                                >
                                                    No discount orders
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        {/* Complimentary */}
                        <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-100 px-4 py-2.5">
                                <SectionHeader
                                    icon="gift"
                                    title="Complimentary"
                                    badge={`Grand total : ${formatSalesValue(compFoodTotal + compBevTotal)}`}
                                />
                            </div>

                            {customers.length > 0 ? (
                                <>
                                    <div className="divide-y divide-gray-100">
                                        {customers.map((customer, i) => {
                                            const allItems = Object.values(customer.items_by_type || {}).flat();
                                            const foodAmt = customer.type_summary?.Food?.total_amount;
                                            const bevAmt = customer.type_summary?.Beverage?.total_amount;

                                            return (
                                                <div
                                                    key={customer.guestID || i}
                                                    className="px-4 py-3"
                                                >
                                                    <div className="mb-3 flex items-start justify-between gap-4">
                                                        <span className="text-sm font-semibold text-gray-900">
                                                            {customer.GuestName}
                                                        </span>

                                                        <span className="text-sm text-gray-600 font-semibold">
                                                            {foodAmt ? `Food : ${formatSalesValue(foodAmt)}` : ""}
                                                            {foodAmt && bevAmt ? " · " : ""}
                                                            {bevAmt ? `Bev : ${formatSalesValue(bevAmt)}` : ""}
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {allItems.map((item, j) => (
                                                            <Pill key={j}>
                                                                {item.total_count} × {item.ItemName} · {formatSalesValue(item.total_amount)}
                                                            </Pill>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-6 border-t border-gray-200 bg-gray-50 px-4 py-3">
                                        <span className="text-sm font-semibold text-gray-800">
                                            Total food : {formatSalesValue(compFoodTotal)}
                                        </span>

                                        <span className="text-sm font-semibold text-gray-800">
                                            Total beverage : {formatSalesValue(compBevTotal)}
                                        </span>

                                        <span className="ml-auto text-sm font-semibold text-gray-800">
                                            Grand total : {formatSalesValue(compFoodTotal + compBevTotal)}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <div className="px-4 py-8 text-center text-sm text-gray-600">
                                    No complimentary items found
                                </div>
                            )}
                        </Card>

                        {/* Voided Items */}
                        <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-100 px-4 py-2.5">
                                <SectionHeader
                                    title="Voided Orders"
                                    badge={`${voidedOrders.length} order${voidedOrders.length !== 1 ? "s" : ""}`}
                                />
                            </div>

                            <div className="overflow-auto tiny-scrollbar">
                                {voidedOrders.length > 0 ? (
                                    voidedOrders.map((order, index) => (
                                        <div
                                            key={index}
                                            className="border-b border-t border-gray-100 first:border-t-0 last:border-b-0 mb-6 last:mb-0"
                                        >
                                            {/* Header */}
                                            <div className="px-4 py-2.5 border-b flex flex-wrap items-center justify-between gap-4 bg-gray-100">
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {Number.isFinite(Number(order.TableNum))
                                                        ? `Table ${order.TableNum}`
                                                        : "Take Away"}
                                                </span>

                                                <div className="flex gap-2 items-center">
                                                    <span className="text-sm font-semibold text-gray-700">
                                                        Employee :
                                                    </span>
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        {order.Employee}
                                                    </span>
                                                </div>

                                                <div className="flex gap-2 items-center">
                                                    <span className="text-sm font-semibold text-gray-700">
                                                        Outlet ID :
                                                    </span>
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        {order.Outlet_orderID}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Items */}
                                            <div className="overflow-x-auto tiny-scrollbar">
                                                <table className="w-full text-sm text-center">
                                                    <thead className="bg-white">
                                                        <tr className="border-b border-gray-200">
                                                            {["Item", "Qty", "Void At", "Reason", "Value"].map((header) => (
                                                                <th
                                                                    key={header}
                                                                    className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-700"
                                                                >
                                                                    {header}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {order.items.map((item, itemIndex) => (
                                                            <tr
                                                                key={itemIndex}
                                                                className="border-b border-gray-100 last:border-0 font-semibold"
                                                            >
                                                                <td className="px-4 py-2.5 text-gray-900">
                                                                    {item.ItemName}
                                                                </td>

                                                                <td className="px-4 py-2.5 text-gray-700">
                                                                    {item.Quantity}
                                                                </td>

                                                                <td className="px-4 py-2.5 text-gray-700">
                                                                    {convertTime(item.voidAt)}
                                                                </td>

                                                                <td className="px-4 py-2.5 text-gray-700">
                                                                    {item.reason || "—"}
                                                                </td>

                                                                <td className="px-4 py-2.5 text-gray-900">
                                                                    {formatSalesValue(
                                                                        Number(item.item_price) * Number(item.Quantity)
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-8 text-center text-sm text-gray-600">
                                        No voided orders found
                                    </div>
                                )}
                            </div>
                        </Card>

                        {/* Reopened Bills */}
                        <Card className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                            <div className="border-b border-gray-100 px-4 py-2.5">
                                <SectionHeader
                                    title="Reopened Bills"
                                    badge={`${reopenedBills.length} bill${reopenedBills.length !== 1 ? "s" : ""}`}
                                />
                            </div>

                            <div className="max-h-[500px] overflow-auto tiny-scrollbar">
                                <table className="w-full text-sm text-center">
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr className="border-b border-gray-200">
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Order ID
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Bill No
                                            </th>
                                            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700">
                                                Opened Time
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {reopenedBills.length > 0 ? (
                                            reopenedBills.map((o, i) => (
                                                <tr
                                                    key={o.Outlet_OrderID || i}
                                                    className="border-b border-gray-100 last:border-0 font-semibold"
                                                >
                                                    <td className="px-4 py-2.5 text-gray-700">
                                                        {o.Outlet_OrderID}
                                                    </td>

                                                    <td className="px-4 py-2.5 text-gray-900">
                                                        {o.bill_no || "—"}
                                                    </td>

                                                    <td className="px-4 py-2.5 text-gray-700">
                                                        {o.Opened_Time || "—"}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={3}
                                                    className="px-4 py-8 text-center text-sm text-gray-600"
                                                >
                                                    No reopened orders
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default SidebarDetailModal;